import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Search, Printer, Ban } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { printGiftVoucherSlip } from "@/utils/giftVoucherPrint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const R = (v) => `R ${(Number(v) || 0).toFixed(2)}`;
const d = (iso) => (iso ? new Date(iso).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" }) : "");
const STATUS_STYLE = { ACTIVE: "bg-emerald-100 text-emerald-800", REDEEMED: "bg-slate-200 text-slate-700", EXPIRED: "bg-amber-100 text-amber-800", CANCELLED: "bg-red-100 text-red-700" };

const Stat = ({ label, value, testid }) => (
  <Card><CardContent className="p-4">
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-2xl font-bold" data-testid={testid}>{value}</p>
  </CardContent></Card>
);

export default function GiftVouchers() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setTerm(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const { data: res } = await axios.get(`${API}/gift-vouchers`, { params: { status: status || undefined, search: term || undefined } });
    setData(res);
  }, [status, term]);

  useEffect(() => { load().catch(() => toast.error("Could not load gift vouchers")); }, [load]);

  const cancel = async (code) => {
    if (!window.confirm(`Cancel unused voucher ${code}? Refund the customer separately.`)) return;
    try {
      await axios.post(`${API}/gift-vouchers/${code}/cancel`);
      toast.success("Voucher cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="gift-vouchers-page">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><Gift className="h-7 w-7 text-rose-600" /> Gift Vouchers</h1>
        <p className="text-sm text-slate-500">Sell vouchers at the till (SELL GIFT VOUCHER). Customers pay with them using the GIFT VOUCHER payment button.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Vouchers owed (unspent balance)" value={R(data?.outstanding_balance)} testid="gv-outstanding" />
        <Stat label="Active vouchers" value={data?.active_count ?? 0} testid="gv-active-count" />
        <Stat label="Total sold" value={`${R(data?.total_sold)} (${data?.sold_count ?? 0})`} testid="gv-total-sold" />
      </div>
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search code or name" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="gv-search" />
        </div>
        {["", "ACTIVE", "REDEEMED", "EXPIRED", "CANCELLED"].map((s) => (
          <Button key={s || "all"} variant={status === s ? "default" : "outline"} onClick={() => setStatus(s)} data-testid={`gv-filter-${s || "all"}`}>
            {s || "All"}
          </Button>
        ))}
      </div>
      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Sold</TableHead><TableHead>For</TableHead>
            <TableHead className="text-right">Value</TableHead><TableHead className="text-right">Balance</TableHead>
            <TableHead>Paid by</TableHead><TableHead>Valid until</TableHead><TableHead>Status</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {(data?.vouchers || []).map((v) => (
              <TableRow key={v.code} data-testid={`gv-row-${v.code}`}>
                <TableCell className="font-mono">{v.code}</TableCell>
                <TableCell>{d(v.created_at)}</TableCell>
                <TableCell>{v.buyer_name || "-"}</TableCell>
                <TableCell className="text-right">{R(v.initial_value)}</TableCell>
                <TableCell className="text-right font-semibold">{R(v.balance)}</TableCell>
                <TableCell>{v.payment_method}</TableCell>
                <TableCell>{d(v.expires_at)}</TableCell>
                <TableCell><Badge className={STATUS_STYLE[v.status]}>{v.status}</Badge></TableCell>
                <TableCell className="whitespace-nowrap">
                  {v.status === "ACTIVE" && (
                    <Button size="sm" variant="ghost" onClick={() => printGiftVoucherSlip(v)} title="Reprint" data-testid={`gv-reprint-${v.code}`}>
                      <Printer className="h-4 w-4" />
                    </Button>
                  )}
                  {v.status === "ACTIVE" && v.balance === v.initial_value && (
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => cancel(v.code)} title="Cancel" data-testid={`gv-cancel-${v.code}`}>
                      <Ban className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {data && data.vouchers.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">No gift vouchers yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
